import type {Props as StyleProps} from '../WidgetStyles';
import type {PostEditing} from '@wordpress/edit-post';
import {CONFIG} from '../../../globals/config';
import {PanelColorSettings} from '@wordpress/block-editor';
import {SelectControl, TextControl} from '@wordpress/components';
import {__} from '@wordpress/i18n';

import styles from './section.pcss';

export type AllFields = {
	current_page_color: string;
	current_page_bg_color: string;
	current_page_font_weight: string;
	current_page_font_size: string;
	current_page_hover_color: string;
	current_page_hover_bg_color: string;
	current_page_parent_color: string;
	current_page_parent_bg_color: string;
	current_page_parent_font_weight: string;
	current_page_parent_font_size: string;
	current_page_parent_hover_color: string;
	current_page_parent_hover_bg_color: string;
	parent_page_color: string;
	parent_page_bg_color: string;
	parent_page_font_weight: string;
	parent_page_font_size: string;
	parent_page_hover_color: string;
	parent_page_hover_bg_color: string;
	child_page_color: string;
	child_page_bg_color: string;
	child_page_font_weight: string;
	child_page_font_size: string;
	child_page_hover_color: string;
	child_page_hover_bg_color: string;
	grandchild_page_color: string;
	grandchild_page_bg_color: string;
	grandchild_page_font_weight: string;
	grandchild_page_font_size: string;
	grandchild_page_hover_color: string;
	grandchild_page_hover_bg_color: string;
};

export type SectionData = {
	label: string;
	prefix: Sections;
}

export type Bullets = {
	circle: string;
	decimal: string;
	disc: string;
	square: string;
	'upper-alpha': string;
	'lower-alpha': string;
	none: string;
}

export type Sections =
	'current_page' |
	'current_page_parent' |
	'parent_page' |
	'child_page' |
	'grandchild_page';

export type Fields = {
	_color: string;
	_bg_color: string;
	_font_weight: string;
	_font_size: string;
	_hover_color: string;
	_hover_bg_color: string;
}

export type FontWeights = {
	[ weight: string ]: string;
}


type Props = Omit<StyleProps, 'name'> & {
	post: PostEditing | null,
	section: SectionData;
}

const fontWeights = [ {
	label: __( '- default -', 'advanced-sidebar-menu' ),
	value: '',
} ];
Object.entries( CONFIG.styles.weights ).forEach( ( [ value, label ] ) => {
	fontWeights.push( {value, label} );
} );

/**
 * Section of settings for a menu level.
 *
 * 1. Current Item.
 * 2. Current Item Parent.
 * 3. Highest Level Parent.
 * 4. Child.
 * 5. Grandchild.
 */
const Section = ( {attributes, setAttributes, section, post}: Props ) => {
	return (
		<div className={styles.wrap}>
			<h3>{section.label}</h3>
			{post && 'current_page' === section.prefix && 0 === post.parent &&
				<p className={'description'}>
					{__( 'Overrides Highest Level Parent', 'advanced-sidebar-menu' )}
				</p>}
			{post && 'parent_page' === section.prefix && 0 === post.parent &&
				<p className={'description'}>
					{__( 'Overridden by Current Item', 'advanced-sidebar-menu' )}
				</p>}
			<PanelColorSettings
				className={styles.colors}
				colorSettings={Object.keys( CONFIG.styles.fields )
					.filter( field => field.includes( 'color' ) )
					.map( field => {
						return {
							value: attributes[ section.prefix + field ],
							onChange: color => setAttributes( {
								[ section.prefix + field ]: color,
							} ),
							label: CONFIG.styles.fields[ field ],
						};
					} )}
			/>
			<SelectControl
				label={__( 'Font weight', 'advanced-sidebar-menu' )}
				labelPosition={'side'}
				value={attributes[ section.prefix + '_font_weight' ] ?? ''}
				options={fontWeights}
				onChange={value => {
					setAttributes( {
						[ section.prefix + '_font_weight' ]: value,
					} );
				}} />
			<TextControl
				className={styles.size}
				label={__( 'Font size (px)', 'advanced-sidebar-menu' )}
				step={1}
				min={2}
				size={5}
				value={attributes[ section.prefix + '_font_size' ] ?? ''}
				type={'number'}
				onChange={value => {
					setAttributes( {
						[ section.prefix + '_font_size' ]: value,
					} );
				}} />
		</div>
	);
};

export default Section;
